import { Permission } from "../authSchema";
import { createContext, useContext } from "react";
import { FetcherBase } from "./fetcher";
import { authManager } from "../global";
import { Role } from "../auth/types";

export function useAuth(): Auth {
    return useContext(AuthContext);
}

export const AuthContext = createContext(undefined);

export class Auth extends FetcherBase {
    private _roles: Role[];
    private _rolesFetchFailed: boolean;
    private _fetchGeneration = 0;

    constructor() {
        super();
        this._roles = [];
        this._rolesFetchFailed = false;
    }

    get isLoggedIn(): boolean {
        return authManager.isLoggedIn;
    }

    /** True when it is not known whether the user is logged in, because the login service could not be asked. */
    get sessionUnavailable(): boolean {
        return authManager.sessionUnavailable;
    }

    get hasConferenceRole(): boolean {
        return this._roles.length > 0;
    }

    /** True when the roles could not be fetched, which is not the same as the user having no roles. */
    get rolesFetchFailed(): boolean {
        return this._rolesFetchFailed;
    }

    hasPermission(permission: Permission): boolean {
        for (const role of this._roles) {
            if (role.permissions.includes(permission)) return true;
        }
        return false;
    }

    /** A copy holding the same data, so that React re-renders the consumers after a refetch. */
    clone(): Auth {
        const copy = new Auth();

        copy._roles = this._roles;
        copy._rolesFetchFailed = this._rolesFetchFailed;

        return copy;
    }

    async fetch(): Promise<void> {
        const generation = ++this._fetchGeneration;
        if (!this.isLoggedIn) {
            this._roles = [];
            this._rolesFetchFailed = false;
            return;
        }

        try {
            const roles = await authManager.fetchRoles();
            // A renewal may start another fetch before an older token's response arrives.
            if (generation !== this._fetchGeneration) return;

            this._roles = roles;
            this._rolesFetchFailed = false;
        } catch (e) {
            if (generation !== this._fetchGeneration) return;

            // The application must still start, the missing roles are reported instead of the main page.
            console.error("failed to fetch the roles of the user", e);
            this._roles = [];
            this._rolesFetchFailed = true;
        }
    }
}
